import { ReservedValues } from "@/constants/reservedValues";
import { t } from "@lingui/core/macro";
import type { GuiTimeZone } from "@mm/metx-workbench.meteomatics.com";
import { compile } from "ejs";
import type { GeoJSON } from "geojson";
import { DateTime } from "luxon";
import template from "./templates/lighting.ejs?raw";

const compiledTemplate = compile(template);

type LightningFeatureLike = {
  geometry: GeoJSON.Geometry;
  properties: GeoJSON.GeoJsonProperties;
};

export type LightningTooltipRow = Readonly<{
  coordinates?: readonly [number, number];
  strikeTime: DateTime | null;
  polarity: "+" | "-" | null;
  currentKa: number | null;
  lightningType: "cloud_to_cloud" | "cloud_to_ground" | null;
}>;

export function createLightingTooltipFromRows(rows: readonly LightningTooltipRow[]) {
  const data = rows.map((row) => {
    const strikeTime = row.strikeTime ? row.strikeTime.toISO({ suppressMilliseconds: true }) : t`No Information`;
    const current = row.currentKa === null ? t`N/A` : Math.trunc(row.currentKa);
    const polarity = row.polarity ?? t`N/A`;
    const lightningType =
      row.lightningType === "cloud_to_cloud"
        ? t`Cloud-to-Cloud`
        : row.lightningType === "cloud_to_ground"
          ? t`Cloud-to-Ground`
          : t`N/A`;
    const coordinate = row.coordinates ? `${row.coordinates[1].toFixed(4)}, ${row.coordinates[0].toFixed(4)}` : "-";
    return { coordinate, strikeTime, polarity, current, lightningType };
  });

  return compiledTemplate({ data });
}

export function createLightingTooltip(features: LightningFeatureLike[], timezone: GuiTimeZone) {
  const rows = features.map((feature): LightningTooltipRow => {
    const coordinates =
      feature?.geometry?.type === "Point" ? (feature.geometry.coordinates as [number, number]) : undefined;

    const strikeTime = feature?.properties
      ? DateTime.fromISO(feature.properties.stroke_time, {
          zone: timezone,
        })
      : null;

    const currentValue = Number.parseInt(feature?.properties?.stroke_current);
    const isReservedCurrent = ReservedValues.includes(currentValue);
    const polarity = isReservedCurrent ? t`N/A` : currentValue > 0 ? "+" : "-";

    const lightningTypeValue = Number.parseInt(feature?.properties?.lightning_type);
    const lightningType: LightningTooltipRow["lightningType"] = ReservedValues.includes(lightningTypeValue)
      ? null
      : lightningTypeValue === 1
        ? "cloud_to_cloud"
        : "cloud_to_ground";

    return {
      coordinates,
      strikeTime,
      polarity: polarity === "+" || polarity === "-" ? polarity : null,
      currentKa: isReservedCurrent ? null : currentValue,
      lightningType,
    };
  });

  return createLightingTooltipFromRows(rows);
}
